import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import {
    getCurrentPromotionsDataView,
    getDiscountTotal,
    getOrderPromotions,
    getShippingPromotions,
} from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import siteMessage from "@insite/client-framework/SiteMessage";
import { canPlaceOrder, canSubmitForApprovalOrder } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPageLinkByPageType, useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CartTotalDisplay, { CartTotalDisplayStyles } from "@sunSource/Widgets/NewOnePageCheckout/CartTotalDisplay";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import placeOrderForApproval from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/PlaceOrderForApproval";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import setIsPlacingOrder from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetIsPlacingOrder";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import { HasHistory } from "@insite/mobius/utilities/HistoryContext";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";

const enum fields {
    termsAndConditionsLink = "termsAndConditionsLink",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.termsAndConditionsLink]: LinkFieldValue;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = state.pages.onePageCheckout;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const { value: cart } = getCurrentCartState(state);
    const promotions = getCurrentPromotionsDataView(state).value;
    return {
        cart: cartState.value,
        orderPromotions: promotions ? getOrderPromotions(promotions) : undefined,
        shippingPromotions: promotions ? getShippingPromotions(promotions) : undefined,
        discountTotal: promotions ? getDiscountTotal(promotions) : undefined,
        isLoading: !cartState.value || cartState.isLoading,
        cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
        placeOrderErrorMessage: state.pages.checkoutReviewAndSubmit.placeOrderErrorMessage,
        showPlaceOrderButton: canPlaceOrder(cart),
        isExpanded: state.pages.onePageCheckout.paymentDetailsState === "complete" || canSubmitForApprovalOrder(cart),
        isPlacingOrder: state.pages.onePageCheckout.isPlacingOrder,
        showSubmitForApprovalOrderButton: canSubmitForApprovalOrder(cart),
        orderApprovalDetailsPageUrl: getPageLinkByPageType(state, "OrderApprovalDetailsPage")?.url,
    };
};

const mapDispatchToProps = {
    setIsPlacingOrder,
    placeOrderForApproval,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface OnePageCheckoutOrderSummaryStyles {
    container?: GridContainerProps;
    centeringWrapper?: InjectableCss;
    titleGridItem?: GridItemProps;
    titleText?: TypographyPresentationProps;
    editGridItem?: GridItemProps;
    editLink?: LinkPresentationProps;
    placeOrderGridItem?: GridItemProps;
    agreementText?: TypographyPresentationProps;
    placeOrderErrorMessageGridItem?: GridItemProps;
    placeOrderErrorMessageText?: TypographyPresentationProps;
    placeOrderButton?: ButtonPresentationProps;
    cartTotalGridItem?: GridItemProps;
    submitForApprovalButton?: ButtonPresentationProps;
    cartTotal?: CartTotalDisplayStyles;
    pricingDisclaimer?: GridItemProps;
}

export const orderSummaryStyles: OnePageCheckoutOrderSummaryStyles = {
    container: { gap: 20},
    centeringWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            height: 450px;
        `,
    },
    titleGridItem: {
        width: 10,
        css: css`
            align-items: center;
        `,
    },
    titleText: {
        variant: "h5",
        css: css`
            margin-bottom: 0;
        `,
    },
    placeOrderGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    submitForApprovalButton: {
        variant: "primary",
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            width: 100%;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    placeOrderButton: {
        variant: "primary",
        css: css`
            width: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            width: 100%;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    agreementText: {
        size: 13,
        css: css`
            margin-top: 8px;
        `,
    },
    editGridItem: {
        width: 2,
        css: css`
            justify-content: end;
            align-items: center;
        `,
    },
    placeOrderErrorMessageGridItem: { width: 12 },
    placeOrderErrorMessageText: {
        color: "danger",
        css: css`
            margin-top: 5px;
            margin-bottom: 15px;
        `,
    },
    pricingDisclaimer: {
        width: 12,
        css: css`
            > ${TypographyStyle} {
                font-size: 12px;
            }
        `,
    },
    cartTotalGridItem: { width: 12},
    cartTotal: {
        container: {
            gap: 10,
            css: css`
                padding: 0;
                background-color: transparent;
            `,
        },
    },
};

const styles = orderSummaryStyles;

const OnePageCheckoutOrderSummary = ({
    fields,
    cart,
    orderPromotions,
    shippingPromotions,
    discountTotal,
    isLoading,
    showPlaceOrderButton,
    isPlacingOrder,
    history,
    placeOrderErrorMessage,
    cartUrl,
    isExpanded,
    setIsPlacingOrder,
    showSubmitForApprovalOrderButton,
    orderApprovalDetailsPageUrl,
    placeOrderForApproval,
}: Props) => {
    if (isLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner data-test-selector="onePageCheckout_orderSummary" />
            </StyledWrapper>
        );
    }
    // const { url: termsAndConditionsUrl } = useGetLink(fields.termsAndConditionsLink);
    // const placeOrderClickHandler = () => {
    //     setIsPlacingOrder({ isPlacingOrder: true });
    // };

    // const submitForApprovalOrderClickHandler = () => {
    //     placeOrderForApproval({
    //         onSuccess: (cartId: string) => {
    //             if (orderApprovalDetailsPageUrl) {
    //                 history.push(`${orderApprovalDetailsPageUrl}?cartId=${cartId}`);
    //             }
    //         },
    //         onComplete(resultProps) {
    //             if (resultProps.apiResult?.cart.id) {
    //                 // "this" is targeting the object being created, not the parent SFC
    //                 // eslint-disable-next-line react/no-this-in-sfc
    //                 this.onSuccess?.(resultProps.apiResult.cart.id);
    //             }
    //         },
    //     });
    // };

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.cartTotalGridItem}>
                <CartTotalDisplay
                    extendedStyles={styles.cartTotal}
                    cart={cart}
                    orderPromotions={orderPromotions}
                    shippingPromotions={shippingPromotions}
                    discountTotal={discountTotal}
                />
            </GridItem>
            <GridItem {...styles.pricingDisclaimer}>
                <Typography>
                    {siteMessage("Checkout_Card_Details_Disclamier")}
                    {siteMessage("Cart_Pricing_Disclamier")}
                </Typography>
            </GridItem>
            {/* {isExpanded && (
                <>
                    <GridItem {...styles.placeOrderGridItem}>
                        {showPlaceOrderButton && (
                            <Button
                                {...styles.placeOrderButton}
                                disabled={isLoading || isPlacingOrder}
                                onClick={placeOrderClickHandler}
                            >
                                {translate("Place Order")}
                            </Button>
                        )}
                        {showSubmitForApprovalOrderButton && (
                            <Button
                                {...styles.submitForApprovalButton}
                                disabled={isLoading || isPlacingOrder}
                                onClick={submitForApprovalOrderClickHandler}
                            >
                                {translate("Submit For Approval")}
                            </Button>
                        )}
                        {showPlaceOrderButton && termsAndConditionsUrl && (
                            <Typography {...styles.agreementText}>
                                {siteMessage("Checkout_Agreement", termsAndConditionsUrl)}
                            </Typography>
                        )}
                    </GridItem>
                    {placeOrderErrorMessage && (
                        <GridItem {...styles.placeOrderErrorMessageGridItem}>
                            <Typography {...styles.placeOrderErrorMessageText}>{placeOrderErrorMessage}</Typography>
                        </GridItem>
                    )}
                </>
            )} */}
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OnePageCheckoutOrderSummary),
    definition: {
        group: "One Page Checkout",
        displayName: "New One page checkout Order Summary",
        fieldDefinitions: [
            {
                name: fields.termsAndConditionsLink,
                fieldType: "Contextual",
                displayName: "Terms And Conditions Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },
        ],
    },
};

export default widgetModule;
